import { nextTick } from 'vue';
import API from "../API";
import utils from '../utils';

export default {
    namespaced: true,

    state: {
        trims: [],
        customer_trim_count: null, // Customer-specific trim count for display

        paginationPageSizes: [10, 25, 50, 100],
    },

    getters: {
        getTrimIndexById: (state) => (id) => {
            return state.trims.findIndex(trim => +trim.id === +id);
        },

        getTrimNameAsUrl: () => (trimId, trimName) => {
            return trimId + '-' + utils.textToUrl(trimName);
        },

        hasCustomerTrims: (state) => {
            return state.customer_trim_count !== null && state.customer_trim_count > 0;
        },

    },

    mutations: {
        setTrims(state, trims) {
            state.trims = trims;
        },

        setCustomerTrimCount(state, count) {
            state.customer_trim_count = count;
        },

        clearCustomerContext(state) {
            state.customer_trim_count = null;
            state.trims = [];
        },

        addTrim(state, trim) {
            state.trims.unshift(trim);
        },

        replaceTrim(state, { index, trim }) {
            state.trims.splice(index, 1, trim);
        },

        removeTrim(state, index) {
            state.trims.splice(index, 1);
        },

    },

    actions: {
        getTrims({rootState, commit}, page) {
            return new Promise((resolve, reject) => {
                const pageSize = rootState['pagination'].pageSize;
                const customerId = rootState['authentication'].customerId;

                // Enhanced logging for customer context
                console.log('🔍 [Trims Store] fetchTrims called with customer context:');
                console.log(`   - CustomerId from auth state: ${customerId}`);
                console.log(`   - Page: ${page}, PageSize: ${pageSize}`);
                
                if (customerId) {
                    console.log(`✅ [Customer Context] Loading trims for customer ${customerId}`);
                } else {
                    console.log(`📋 [General Context] Loading all trims (no customer filter)`);
                }

                API.trim.getTrims(page, pageSize).then(
                    (response) => {
                        let trims = response.data;

                        commit('setTrims', trims);

                        // Set customer-specific trim count if available
                        if (response.customerTrimCount !== undefined) {
                            commit('setCustomerTrimCount', response.customerTrimCount);
                            console.log(`📊 [Customer Stats] Customer ${customerId} has ${response.customerTrimCount} trims total`);
                        }

                        resolve();
                    },
                    (error) => {
                        reject(error);
                    });
            });
        },

        getTrimsByQuery({rootState, commit}, { mainParams, searchParams, page }) {
            return new Promise((resolve, reject) => {
                const { userId } = mainParams;
                const { trimName, description } = searchParams;
                const pageSize = rootState['pagination'].pageSize;
                const customerId = rootState['authentication'].customerId;

                // Enhanced logging for customer context in search
                console.log('🔍 [Trims Store] searchTrimsByQuery called with customer context:');
                console.log(`   - CustomerId: ${customerId}`);
                console.log(`   - UserId: ${userId}`);
                console.log(`   - Search params:`, { trimName, description });
                
                if (customerId) {
                    console.log(`🔎 [Customer Search] Searching customer ${customerId} trims`);
                }

                API.trim.getTrimsByQuery(userId, trimName, description, page, pageSize).then(
                    (response) => {
                        let trims = response.data;

                        commit('setTrims', trims);

                        // Set customer-specific trim count if available
                        if (response.customerTrimCount !== undefined) {
                            commit('setCustomerTrimCount', response.customerTrimCount);
                        }

                        resolve(trims);
                    },
                    (error) => {
                        reject(error);
                    });
            });
        },

        getTrimsCreatedByUser({rootState, commit}, {userId, page}) {
            return new Promise((resolve, reject) => {
                const pageSize = rootState['pagination'].pageSize;
                const customerId = rootState['authentication'].customerId;

                console.log('🔍 [Trims Store] getTrimsCreatedByUser called:');
                console.log(`   - UserId: ${userId}, CustomerId: ${customerId}`);

                API.trim.getTrimsCreatedByUser(userId, page, pageSize).then(
                    (response) => {
                        let trims = response.data;

                        commit('setTrims', trims);

                        // Set customer-specific trim count if available
                        if (response.customerTrimCount !== undefined) {
                            commit('setCustomerTrimCount', response.customerTrimCount);
                        }

                        resolve(trims);
                    },
                    (error) => {
                        reject(error);
                    });
            });
        },

        addNewTrim({commit}, trimData) {
            return new Promise(resolve => {
                API.trim.addNew(trimData).then(
                    (newAddedTrim) => {

                        // points are stored on the backend database in JSON, so they need to be parsed into an Object
                        newAddedTrim['points'] = JSON.parse(newAddedTrim['points']);

                        commit('addTrim', newAddedTrim);

                        resolve(newAddedTrim);
                    },
                    (error) => {
                        console.log(error);
                    });
            });
        },

        setCurrentTrimToEditor({rootState, commit, dispatch}, trimId) {
            return new Promise(resolve => {

                dispatch('editor/resetStatesToInitialState', null, {root: true});

                API.trim.getTrim(trimId).then(
                    async (trim) => {
                        let trimPoints = JSON.parse(trim.points);

                        // set all trim props to the store
                        await dispatch('trimInfo/setTrimInfo', trim, {root: true});

                        await dispatch('paint/setCurrentPaint', trim.paintColorId, {root: true});
                        commit('paint/setPaintSide', trim.paintSide, {root: true});
                        await dispatch('editor/setPoints', trimPoints, {root: true});
                        await dispatch('undoRedo/clearHistory', null, {root: true});

                        if(trim.exported) {
                            // disable the trim editing if it was exported
                            const actionSelect = rootState['editor'].USER_ACTIONS.SELECT;
                            commit('editor/setUserAction', actionSelect, {root: true});
                        }

                        resolve(trim.trimName);
                    },
                    (error) => {
                        console.log(error);
                    });
            });
        },

        async updateTrimInfo({ state, getters, commit }, { trimId, trimName, description }) {
            let trimInfo = {
                trimName,
                description,
            };

            return new Promise((resolve, reject) => {
                API.trim.updateInfo(trimId, trimInfo).then(
                    () => {
                        let newTrim = {
                            id: trimId,
                            ...trimInfo,
                        };

                        if (state.trims.length) {
                            // after update trim data on server, need to update trim data in the trims state
                            let trimIndex = getters.getTrimIndexById(trimId);
                            let oldTrim = state.trims[trimIndex];

                            newTrim = {
                                ...oldTrim,
                                ...trimInfo,
                            };

                            commit('replaceTrim', { index: trimIndex, trim: newTrim });
                        }

                        resolve(newTrim);
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        },

        async updateOpenedTrimStatus({rootState}, openedStatus) {
            const trimId = rootState['trimInfo'].trimId;
            const permissionToEdit = rootState['trimInfo'].permissionToEdit;

            if (!trimId || !permissionToEdit) return;

            return new Promise(resolve => {
                API.trim.updateOpenedStatus(trimId, openedStatus).then(
                    () => {
                        resolve();
                    },
                    (error) => {
                        console.log(error);
                    });
            });
        },

        async saveTrimFromEditor({rootState}) {
            const trimId = rootState['trimInfo'].trimId;
            const isTrimExported = rootState['trimInfo'].isTrimExported;

            if (isTrimExported) return false;

            let dataToSave = {
                points: JSON.stringify(rootState['editor'].points),
                paintColorId: rootState['paint'].currentPaint?.id || null,
                paintSide: rootState['paint'].paintSide,
            };

            return new Promise(resolve => {
                API.trim.save(trimId, dataToSave).then(
                    () => {
                        resolve();
                    },
                    (error) => {
                        console.log(error);
                    });
            });
        },

        async exportTrimFromEditor({rootState, commit, dispatch, rootGetters}) {
            const trimId = rootState['trimInfo'].trimId;
            const isTrimExported = rootState['trimInfo'].isTrimExported;

            if (!isTrimExported) {
                // if the trim is exporting in a first time then save the trim data to the database

                await dispatch('saveTrimFromEditor');
            }

            // before export set "export status" to "exported" to display it correctly on the picture
            commit('trimInfo/setIsTrimExported', true, {root: true});

            // wait for canvas rendering
            await nextTick();

            // export files
            let filesName = rootState['trimInfo'].trimName;
            let fileJSON = await dispatch('exports/dataToSaveAsJSON', null, {root: true});
            let fileImage = await dispatch('exports/exportFilePNG', null, {root: true});

            let dataToExport = {
                filesName,
                fileJSON,
                fileImage,
            };

            // Get customer context from authentication store for export association
            const customerContext = rootGetters['authentication/customerContext'];
            
            // Enhanced logging for customer context debugging
            console.log('🔍 Debug Customer Context Flow:');
            console.log(`   - Authentication state customerId: ${rootState.authentication.customerId}`);
            console.log(`   - Customer context getter result:`, customerContext);
            
            if (customerContext?.customer_id) {
                console.log(`✅ Exporting trim ${trimId} WITH customer association: ${customerContext.customer_id}`);
            } else {
                console.log(`⚠️ Exporting trim ${trimId} WITHOUT customer association - customer_id will be null`);
            }

            return new Promise((resolve, reject) => {
                API.trim.export(trimId, dataToExport, customerContext).then(
                    (response) => {
                        // Enhanced export handling for cross-platform compatibility
                        
                        // Log customer association status
                        if (response.customerAssociation) {
                            const { successful, customer_id, status } = response.customerAssociation;
                            if (successful) {
                                console.log(`✅ Customer association successful: Trim ${trimId} linked to customer ${customer_id}`);
                            } else {
                                console.log(`⚠️ Customer association status: ${status}`);
                            }
                        }
                        
                        // Check if there are download files (both online portal and UNC fallback scenarios)
                        if (response.downloadData) {
                            // Handle client-side download
                            dispatch('downloadExportFiles', response.downloadData, {root: true});
                            
                            // Log any UNC path warnings for regular users
                            if (response.warning) {
                                console.warn('Export warning:', response.warning);
                            }
                            if (response.uncError) {
                                console.log('UNC path details:', response.uncError);
                            }
                        } else if (response.data && response.data.jsonFilePath && response.data.jsonFilePath !== 'local_download_fallback') {
                            // Regular user with successful UNC path export - still download PNG locally
                            const localDownloadData = {
                                filesName: dataToExport.filesName,
                                fileImage: dataToExport.fileImage
                            };
                            
                            // Download only the PNG file for regular users (JSON is saved to UNC path)
                            dispatch('downloadPNGFile', localDownloadData, {root: true});
                        }
                        
                        // disable the trim editing
                        const actionSelect = rootState['editor'].USER_ACTIONS.SELECT;
                        commit('editor/setUserAction', actionSelect, {root: true});

                        resolve(response); // Return full response including customer association status
                    },
                    (error) => {
                        // back the "export status" to "draft"
                        commit('trimInfo/setIsTrimExported', false, {root: true});

                        console.log(error);
                        reject(error);
                    });
            });
        },

        deleteTrim({getters, commit}, trimId) {
            return new Promise(resolve => {
                API.trim.delete(trimId).then(
                    (deletedTrimId) => {
                        let indexToRemove = getters.getTrimIndexById(deletedTrimId);

                        commit('removeTrim', indexToRemove);

                        resolve(deletedTrimId);
                    },
                    (error) => {
                        console.log(error);
                    });
            });
        },

        searchTrim({rootState, commit}, query) {
            return new Promise((resolve, reject) => {
                const customerId = rootState['authentication'].customerId;

                console.log('🔍 [Trims Store] searchTrim called:');
                console.log(`   - Query: "${query}", CustomerId: ${customerId}`);

                API.trim.search(query).then(
                    (trims) => {
                        commit('setTrims', trims);
                        resolve();
                    },
                    (error) => {
                        console.log(error);
                        reject();
                    });
            });
        },

        // Clear trims when customer context changes
        clearTrimsOnCustomerChange({commit}) {
            console.log('🔄 [Customer Context] Clearing trims due to customer context change');
            commit('clearCustomerContext');
        },

        applyPagination({state, dispatch}) {
            const pageSizes = state.paginationPageSizes;

            const currentSize = JSON.parse(localStorage.getItem('trimsPaginationCurrentPageSize'));

            dispatch('pagination/setPageSizes', {pageSizes, currentSize}, { root: true });
        },

        savePaginationPageSize(ctx, pageSize) {
            // save active value to the localStorage
            localStorage.setItem('trimsPaginationCurrentPageSize', pageSize.toString());
        },

    },
}